import React from 'react';
import './TeacherEarnings.css';
import { Box, Typography, CircularProgress } from '@mui/material';
import KoinIcon from '../../../../../assets/Admin/EarningIcons/koin-icons.svg';
import ReceivedIcon from '../../../../../assets/Admin/EarningIcons/received-icon.svg';
import DisputeIcon from '../../../../../assets/Admin/EarningIcons/dispute-icon.svg';
import { useState, useEffect } from 'react';
import TeacherEarningsViewAll from './TeacherEarningsViewAll/TeacherEarningsViewAll';
import OneOnOneSessionIcon from '../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import { axiosInstance } from '../../../../../utils/tokenUtils';

// Clear mock data
export const EarningHistory = [];

function formatText(input) {
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

export const EarningHistoryMap = {
  received: {
    class: 'received-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-earning-history-received-amount-text',
  },
  dispute: {
    class: 'dispute-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-earning-history-dispute-amount-text',
  },
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#279BE2',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeSessionIcon,
  },
};

const TeacherEarnings = ({ teacherId }) => {
  const [openViewAll, setOpenViewAll] = useState(false);
  const [walletDetails, setWalletDetails] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [conversionRate, setConversionRate] = useState(2); // Default fallback value
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch conversion rate
        const conversionResponse = await axiosInstance.get('/auth/koins-to-riyal');
        setConversionRate(conversionResponse.data.conversion_rate);

        // Fetch wallet details
        const walletResponse = await axiosInstance.get(
          `/admin/teachers/${teacherId}/wallet_details`
        );
        setWalletDetails(walletResponse.data);

        // Fetch transaction history
        const historyResponse = await axiosInstance.get(
          `/admin/teachers/${teacherId}/transaction-history`
        );
        setTransactionHistory(historyResponse.data.results || []);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.detail || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    if (teacherId) {
      fetchData();
    }
  }, [teacherId]);

  const handleViewAll = () => {
    setOpenViewAll(true);
  };

  // Calculate koin values using real-time conversion rate
  const calculateKoins = usdAmount => {
    if (!usdAmount || !conversionRate) return 0;

    // Handle both string and number inputs
    let numericAmount;
    if (typeof usdAmount === 'string') {
      numericAmount = parseFloat(usdAmount.replace(/[^0-9.-]+/g, ''));
    } else {
      numericAmount = parseFloat(usdAmount);
    }

    return isNaN(numericAmount) ? 0 : Math.round(numericAmount * conversionRate);
  };

  // Transform API data to match the expected format
  const transformTransactionData = transactions => {
    return transactions.map(transaction => ({
      id: transaction.id,
      type: ['dispute', 'penalty', 'penalties', 'session_penalty'].includes(
        transaction.payment_type
      )
        ? 'dispute'
        : 'received',
      title: `${transaction.session_type} Session`,
      session_type: transaction.session_type,
      date: new Date(transaction.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      amount: `${transaction.koins} Koins`,
      teacher_name: transaction.teacher_name,
      session_date: transaction.session_date,
      session_duration: transaction.session_duration,
    }));
  };

  const recentTransactions = transformTransactionData(transactionHistory.slice(0, 5));

  console.log(walletDetails);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px', // Adjust this value based on your needs
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#0A5EF3' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          padding: '20px',
          textAlign: 'center',
          color: 'error.main',
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <>
      {openViewAll === false ? (
        <Box className="flex flex-col gap-4">
          <Box className="flex flex-wrap gap-4">
            <Box className="total-earning-container">
              <div className="total-earning-text">Total Earnings</div>
              <Box className="flex gap-3 flex-col">
                <div className="total-amount-value">
                  {calculateKoins(walletDetails?.total_earnings || '0')} Riyal
                </div>
                <Box className="flex gap-2">
                  <img src={KoinIcon} alt="Koin" width="20" height="20" />
                  <div className="koin-amount"> {walletDetails?.total_earnings || '0'}</div>
                </Box>
              </Box>
            </Box>
            <Box className="locked-earning-container">
              <div className="locked-earning-text">Locked Earning</div>
              <div className="locked-amount-value">
                {calculateKoins(walletDetails?.locked_earnings || '0')} Riyal
              </div>
              <Box className="flex items-center gap-2">
                <img src={KoinIcon} alt="Koin" width="20" height="20" />
                <div className="koin-amount"> {walletDetails?.locked_earnings || '0'}</div>
              </Box>
            </Box>
          </Box>
          <Box className="flex justify-between items-center">
            <div className="history-text">History</div>
            <div className="view-all" onClick={handleViewAll}>
              View All
            </div>
          </Box>
          <Box className="flex flex-col gap-4">
            {recentTransactions && recentTransactions.length > 0 ? (
              recentTransactions.map(item => {
                return (
                  <Box className="single-earning-history-container" key={item?.id}>
                    <Box className="flex items-center gap-4">
                      <Box className={EarningHistoryMap[item?.type]?.class}>
                        <img src={EarningHistoryMap[item?.type]?.icon} alt={item?.type} />
                      </Box>
                      <Box className="flex flex-col gap-1">
                        <div className="single-earning-history-title-text">
                          {formatText(item?.title)}
                        </div>
                        <div className="single-earning-history-title-text">
                          By {item?.teacher_name}
                        </div>
                      </Box>
                    </Box>
                    <Box className="flex gap-2">
                      <img src={SessionTypeColor[item?.session_type]?.icon} alt="Default icon" />
                      <Typography
                        className="session-type-text"
                        sx={{
                          color: SessionTypeColor[item?.session_type]?.color,
                        }}
                      >
                        {item?.session_type ? SessionTypeColor[item?.session_type]?.value : '---'}
                      </Typography>
                    </Box>
                    <div className="date-time-value">{item?.date}</div>
                    <div className={EarningHistoryMap[item?.type]?.amountClass}>
                      {item?.type === 'received' ? '+' : '-'} {item?.amount}
                    </div>
                  </Box>
                );
              })
            ) : (
              <Box className="flex justify-center items-center py-4">
                <Typography variant="body1" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <TeacherEarningsViewAll teacherId={teacherId} conversionRate={conversionRate} />
      )}
    </>
  );
};

export default TeacherEarnings;
